/**
 * Small, theme-aware UI primitives used across the booking wizard.
 *
 * Keeping these focused and inline-styled means the booking flow doesn't
 * leak any custom CSS into the global stylesheet — the rest of the project
 * stays untouched.
 */

import { useTranslation } from "react-i18next";

/* -- Responsive helpers --------------------------------------------------
 *
 * The booking flow runs entirely on inline styles to avoid leaking CSS
 * into the global stylesheet. We use `clamp()` for paddings + font sizes
 * so the same component renders comfortably at 320px and 1440px without
 * any media queries.
 *
 * - PAD_X  / PAD_Y     – generic content padding inside a StepCard
 * - HEAD_PAD_*         – top of a step (slightly more breathing room)
 * - FOOTER_PAD_*       – button row at the bottom
 */
const PAD_X = "clamp(14px, 4vw, 24px)";
const HEAD_PAD_TOP = "clamp(18px, 4vw, 24px)";
const FOOTER_PAD_TOP = "clamp(12px, 3vw, 16px)";
const FOOTER_PAD_BOTTOM = "clamp(14px, 3.5vw, 20px)";

export const BOOKING_FONT_FAMILY =
  '"Segoe UI", "Noto Sans Armenian", "Noto Sans", Arial, sans-serif';

/** Card surface used as the host for every step's content. */
export function StepCard({ palette, children, style }) {
  return (
    <div
      className="booking-step-card"
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: "clamp(14px, 3vw, 20px)",
        boxShadow: palette.shadow,
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Header row inside a StepCard: title + optional subtitle. */
export function StepHeader({ palette, title, subtitle, accessory }) {
  return (
    <div
      className="booking-step-header d-flex flex-wrap align-items-start justify-content-between gap-2"
      style={{
        padding: `${HEAD_PAD_TOP} ${PAD_X} 8px ${PAD_X}`,
      }}
    >
      <div className="booking-step-header__copy" style={{ flex: 1, minWidth: 0 }}>
        <h3
          className="mb-1 fw-700"
          style={{
            fontFamily: BOOKING_FONT_FAMILY,
            color: palette.text,
            fontSize: "clamp(1.1rem, 3.2vw, 1.4rem)",
            lineHeight: 1.25,
          }}
        >
          {title}
        </h3>
        {subtitle ? (
          <p
            className="mb-0"
            style={{
              color: palette.textMuted,
              fontSize: "clamp(0.85rem, 2.2vw, 0.95rem)",
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {accessory ? <div className="booking-step-header__accessory">{accessory}</div> : null}
    </div>
  );
}

/** Row of action buttons docked at the bottom of a step. */
export function StepFooter({ palette, children, sticky = false }) {
  const stickyStyle = sticky
    ? { position: "sticky", bottom: 0, background: palette.surface, zIndex: 2 }
    : {};
  return (
    <div
      className="booking-step-footer d-flex flex-wrap gap-2 justify-content-between align-items-center"
      style={{
        padding: `${FOOTER_PAD_TOP} ${PAD_X} ${FOOTER_PAD_BOTTOM} ${PAD_X}`,
        borderTop: palette.border,
        ...stickyStyle,
      }}
    >
      {children}
    </div>
  );
}

/** Padding constants exported for step-content panels. Keeping them in
 *  one place means a single tweak propagates to every wizard screen. */
export const STEP_PAD = {
  x: PAD_X,
  contentTop: "clamp(10px, 2.5vw, 16px)",
  contentBottom: "clamp(8px, 2vw, 16px)",
};

export function ArrowRightIcon({
  color = "currentColor",
  size = 18,
  strokeWidth = 2,
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: size * 0.42,
          height: size * 0.42,
          borderTop: `${strokeWidth}px solid currentColor`,
          borderRight: `${strokeWidth}px solid currentColor`,
          transform: "rotate(45deg)",
          display: "block",
        }}
      />
    </span>
  );
}

/** Selection card used for industries / orgs / branches / services. */
export function SelectableCard({
  palette,
  selected,
  onClick,
  icon,
  iconImg,
  title,
  subtitle,
  badge,
  trailing,
  disabled,
}) {
  const borderColor = selected ? palette.accentBorder : "transparent";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="text-start w-100"
      style={{
        background: selected ? palette.accentSoft : palette.surfaceAlt,
        border: `2px solid ${borderColor}`,
        borderRadius: 14,
        padding: "clamp(12px, 3vw, 14px) clamp(12px, 3.5vw, 16px)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background 0.15s ease, border-color 0.15s ease",
        display: "flex",
        gap: "clamp(10px, 2.5vw, 12px)",
        alignItems: "center",
        color: palette.text,
        boxShadow: selected ? palette.shadowSoft : "none",
      }}
      onMouseEnter={(e) => {
        if (disabled || selected) return;
        e.currentTarget.style.background = palette.surfaceHover;
      }}
      onMouseLeave={(e) => {
        if (disabled || selected) return;
        e.currentTarget.style.background = palette.surfaceAlt;
      }}
    >
      {iconImg ? (
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: palette.surface,
            border: palette.border,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <img
            src={iconImg}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </span>
      ) : icon ? (
        <span
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: selected ? palette.accent : palette.surface,
            color: selected ? "#fff" : palette.accent,
            border: selected ? "1px solid transparent" : palette.border,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            flexShrink: 0,
          }}
        >
          <i className={icon} aria-hidden="true" />
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="d-block fw-600"
          style={{
            color: palette.text,
            fontSize: "1rem",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            className="d-block"
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              marginTop: 2,
              wordBreak: "break-word",
            }}
          >
            {subtitle}
          </span>
        ) : null}
        {badge ? (
          <span
            className="d-inline-block mt-1"
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 999,
              background: palette.accentSoft,
              color: palette.accent,
            }}
          >
            {badge}
          </span>
        ) : null}
      </span>
      {trailing ? (
        <span
          style={{
            color: palette.textMuted,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {trailing}
        </span>
      ) : null}
      {selected ? (
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: palette.accent,
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.85rem",
            flexShrink: 0,
          }}
        >
          <i className="las la-check" />
        </span>
      ) : null}
    </button>
  );
}

/** Skeleton row used while async data is loading. */
export function SkeletonRow({ palette, count = 4 }) {
  const items = Array.from({ length: count });
  return (
    <div className="d-grid gap-2">
      {items.map((_, i) => (
        <div
          key={i}
          style={{
            height: 70,
            borderRadius: 14,
            background: palette.surfaceAlt,
            border: palette.border,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.05) 50%, transparent 100%)",
              animation: "bookingShimmer 1.4s linear infinite",
            }}
          />
        </div>
      ))}
      <style>{`@keyframes bookingShimmer { 0% { transform: translateX(-100%) } 100% { transform: translateX(100%) } }`}</style>
    </div>
  );
}

/** Empty / error state with optional retry button. */
export function EmptyState({ palette, icon, title, subtitle, action }) {
  return (
    <div
      className="text-center"
      style={{
        padding: "clamp(24px, 5vw, 32px) clamp(12px, 3vw, 16px)",
        color: palette.textMuted,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          margin: "0 auto 12px auto",
          borderRadius: "50%",
          background: palette.surfaceAlt,
          border: palette.border,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "2rem",
          color: palette.accent,
        }}
      >
        <i className={icon || "las la-inbox"} aria-hidden="true" />
      </div>
      <div
        className="fw-600 mb-1"
        style={{ color: palette.text, fontSize: "1.05rem" }}
      >
        {title}
      </div>
      {subtitle ? (
        <div style={{ color: palette.textMuted, fontSize: "0.9rem" }}>
          {subtitle}
        </div>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/** Search input — used in organization picker. */
export function SearchInput({ palette, value, onChange, placeholder }) {
  return (
    <div
      className="d-flex align-items-center"
      style={{
        background: palette.surfaceAlt,
        border: palette.border,
        borderRadius: 12,
        padding: "8px 14px",
      }}
    >
      <i
        className="las la-search"
        style={{
          color: palette.textMuted,
          fontSize: "1.2rem",
          marginRight: 8,
        }}
        aria-hidden="true"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          background: "transparent",
          border: "none",
          outline: "none",
          color: palette.text,
          flex: 1,
          fontSize: "0.95rem",
        }}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          style={{
            background: "transparent",
            border: "none",
            color: palette.textMuted,
            cursor: "pointer",
            fontSize: "1rem",
            padding: 0,
          }}
          aria-label="Clear"
        >
          <i className="las la-times" />
        </button>
      ) : null}
    </div>
  );
}

/** Inline retry button for error states. */
export function RetryButton({ palette, onClick, label }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="pill fw-500"
      style={{
        background: palette.accent,
        color: "#fff",
        border: "none",
        padding: "8px 20px",
      }}
    >
      <i className="las la-redo me-1" aria-hidden="true" />
      {label || t("booking.common.retry")}
    </button>
  );
}
